<script lang="ts">
	import { marketsConfig } from '$lib/config/markets';
	import {
		addErrorNotification,
		addTransactionNotification
	} from '$lib/notifications/notificationManager';
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import {
		AUTOSWAP_OP,
		AUTOSWAP_STABLES,
		AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION
	} from '$lib/settings';
	import type { Order } from '$lib/types/order';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { getMarketCurrentMarketPrice } from '$lib/util/marketHelper';
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';
	import { getNumBackInUnits, addDollarSign, toBN } from '$lib/util/util';
	import {
		cancelFullOrderSmartContract,
		cancelLimitTradeSmartContract
	} from '$lib/web3/actions/tradeLimit';
	import { cancelTradeSmartContract, executeStandardOrder } from '$lib/web3/actions/tradeStandard';
	import { getParsedError } from '$lib/web3/errorHandling';
	import type { SendTransactionResult } from '@wagmi/core';
	import BigNumber from 'bignumber.js';
	import { get } from 'svelte/store';
	import { smartContractWalletAddress } from '../scwallet/store';
	import { getDisplayAsset } from '../tradingView/util';
	import {
		cancellingAllOrders,
		expiredOrders,
		manuallyExecutingOrder,
		marginsWithoutPositions,
		priceImpactDelta,
		retryingOrder
	} from '../userInteract/store';
	import { manuallyExecuteDelayedOrder } from '$lib/web3/actions/orders';
	import { EvmPriceServiceConnection } from '@pythnetwork/pyth-evm-js';
	import {
		checkIfManuallyExecutable,
		getTakeProfitAndStopLossFromOrder,
		isEntirePositionTPSLOrder
	} from '../util';
	import { markets, tokensAllowedForDepositBalance } from '$lib/home/store';
	import type { Market } from '$lib/types/market';
	import { LARGE_NEGATIVE_INT, LARGE_POSITIVE_INT } from '$lib/web3/contants';
	import { getShouldUpdateKeeperFee } from '$lib/data/userInteract';
	import { amountPerTokenToSwap, matchaCallData } from '$lib/home/store';
	import { tokensAllowedForDeposit } from '../scwallet/config';
	import { updateData } from '../userInteract/userInteract';
	import { ordersRaw } from '$store/orders';
	import { get_pyth_url } from '$lib/apis/utils';

	export let ord: Order;

	let isCancelling: boolean = false;
	$: if ($cancellingAllOrders) {
		isCancelling = true;
	} else {
		isCancelling = false;
	}

	let cancellable: boolean = false;
	$: {
		if (ord.status == 'Expired' || ord.orderType == 'Limit' || ord.orderType == 'Advanced') {
			cancellable = true;
		} else {
			cancellable = false;
		}
	}

	let orderMinAge: string;
	$: orderMinAge =
		$markets.filter((m: Market) => {
			return m.asset === ord.asset;
		})[0]?.orderMinAge ?? 0;

	const isPairOrder = ord.priceA == null && ord.priceB == null;
	const isLong = isPairOrder ? new BigNumber(ord.size).lt(0) : new BigNumber(ord.size).gt(0);
	let { takeProfit, stopLoss } = getTakeProfitAndStopLossFromOrder(ord, isLong);

	const orderSize =
		ord.status == 'FirstExecuted' && ord.totalFee
			? new BigNumber(ord.size).multipliedBy(-1).toString()
			: ord.size;

	async function cancelOrder() {
		try {
			isCancelling = true;
			let tx: SendTransactionResult;
			if (ord.orderType == 'Limit') {
				tx = await cancelLimitTradeSmartContract(
					ord.requestId,
					get(smartContractWalletAddress) as `0x${string}`
				);
			} else if (ord.orderType == 'Advanced') {
				tx = await cancelFullOrderSmartContract(
					ord.orderId.toString(),
					get(smartContractWalletAddress) as `0x${string}`
				);
			} else {
				tx = await cancelTradeSmartContract(
					ord.marketIdentifier,
					get(smartContractWalletAddress) as `0x${string}`
				);
			}
			addTransactionNotification(
				'Order Cancelled',
				'Your order has been cancelled.',
				tx,
				async () => {
					ordersRaw.apiCall({});
					isCancelling = false;
				}
			);
		} catch (e: any) {
			const { title, description } = getParsedError(e);
			isCancelling = false;
			addErrorNotification(title, description, JSON.stringify(e));
		}
	}

	async function retryOrder() {
		try {
			$retryingOrder = true;
			const shouldUpdateKeeperFee = await getShouldUpdateKeeperFee();
			let tx: SendTransactionResult;
			const { takeProfit, stopLoss } = getTakeProfitAndStopLossFromOrder(
				ord,
				new BigNumber(ord.size).gt(0)
			);
			tx = await executeStandardOrder(
				'0',
				ord.size,
				ord.expiry,
				ord.marketIdentifier,
				get(priceImpactDelta),
				get(marginsWithoutPositions),
				get(tokensAllowedForDepositBalance),
				get(expiredOrders),
				takeProfit,
				stopLoss,
				false,
				get(smartContractWalletAddress) as `0x${string}`,
				get(AUTOWITHDRAW_MARGIN_FROM_CLOSEPOSITION),
				shouldUpdateKeeperFee,
				get(amountPerTokenToSwap),
				get(matchaCallData),
				get(AUTOSWAP_STABLES),
				get(AUTOSWAP_OP),
				get(tokensAllowedForDeposit)
			);
			addTransactionNotification(
				'Expired Order Re-placed',
				'Your expired order has been placed again',
				tx,
				() => {
					updateData();
					$retryingOrder = false;
				}
			);
			// resetting these for next transaction
			matchaCallData.set({});
			amountPerTokenToSwap.set({});
			$retryingOrder = false;
		} catch (e: any) {
			const { title, description } = getParsedError(e);
			addErrorNotification(title, description, JSON.stringify(e));
		}
	}

	async function manualExecution() {
		$manuallyExecutingOrder = true;
		let tx: SendTransactionResult;
		try {
			const PYTH_URL = get_pyth_url();
			const connection = new EvmPriceServiceConnection(PYTH_URL);
			const priceIds = [marketsConfig[ord.asset]['pythFeedKey']];
			const priceUpdateData = await connection.getPriceFeedsUpdateData(priceIds);
			tx = await manuallyExecuteDelayedOrder(
				get(smartContractWalletAddress) as `0x${string}`,
				ord.marketIdentifier,
				priceUpdateData
			);
			addTransactionNotification(
				'Order Manually Executed',
				'Your Order has been executed',
				tx,
				() => {
					$manuallyExecutingOrder = false;
				}
			);
		} catch (e: any) {
			$manuallyExecutingOrder = false;
			const { title, description } = getParsedError(e);
			addErrorNotification(title, description, JSON.stringify(e, Object.getOwnPropertyNames(e)));
		}
	}

	function getOrderType() {
		if (ord.orderType == 'Limit') {
			return 'Limit Order';
		} else if (ord.orderType == 'Advanced') {
			if (ord.status == 'FirstExecuted') return 'TP/SL Order';
			let advancedOrderText = 'Range Order';
			if (ord.size && new BigNumber(ord.size).gt(0)) {
				advancedOrderText =
					ord.priceA && new BigNumber(ord.priceA).eq(1) ? 'Limit Order' : 'Stop Order';
			} else {
				advancedOrderText =
					ord.priceA && new BigNumber(ord.priceA).eq(1) ? 'Stop Order' : 'Limit Order';
			}
			if (ord.firstPairA && new BigNumber(ord.firstPairA).gt(0)) {
				advancedOrderText += ' with TP/SL';
			}
			return advancedOrderText;
		} else if (ord.orderType == 'DelayedOffchain') {
			return 'Market Order';
		} else {
			return '-';
		}
	}

	function getOrderStatus() {
		if (ord.status == 'FirstExecuted') {
			return 'Active';
		} else return ord.status;
	}

	$: marketDecimals =
		ord && ord.asset && marketsConfig[ord.asset] && marketsConfig[ord.asset]?.showDecimals
			? marketsConfig[ord.asset].showDecimals
			: 2;
</script>

<!-- Desktop View -->
<div class="hidden sm:grid grid-cols-8 border-t border-t-fx-grey-600 py-4 items-center px-2">
	<div class="flex gap-2">
		<img
			loading="lazy"
			src={getTokenLogo(ord.marketIdentifier)}
			alt="logo"
			class="w-5 h-5 rounded-full"
		/>
		<div class="text-fx-grey-100 text-sm rounded-lg squircle-lg font-medium">
			{getDisplayName(ord.marketIdentifier)}
		</div>
	</div>
	<GradientBorder
		gradient="bg-fx-gradient-6"
		paddingClass="p-px"
		borderRadius="rounded-lg squircle-lg"
		outerClassList="w-fit my-auto mx-auto h-fit"
		innerClassList="/''bg-fx-black"
	>
		{#if ord.status == 'FirstExecuted' && ord.totalFee ? new BigNumber(ord.size).lt(0) : new BigNumber(ord.size).gt(0)}
			<div
				class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg squircle-lg"
			>
				Long
			</div>
		{:else}
			<div
				class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg squircle-lg"
			>
				Short
			</div>
		{/if}
	</GradientBorder>
	<div class="flex flex-col gap-1 items-start justify-center mx-auto">
		<div class="flex flex-col gap-1 items-start justify-center mx-auto">
			<div class="text-fx-grey-100 text-xs font-medium text-center">
				{isEntirePositionTPSLOrder(ord)
					? 'Entire Position'
					: getNumBackInUnits(orderSize, 18, 4) + ' ' + getDisplayAsset(ord?.asset)}
			</div>
		</div>
	</div>
	<div class="text-fx-grey-100 text-xs font-medium text-center">
		{getOrderType()}
	</div>

	<div class="text-fx-grey-100 text-xs font-medium text-center">
		{#if ord.orderType == 'Limit'}
			{addDollarSign(getNumBackInUnits(ord?.triggerPrice, 18, marketDecimals), marketDecimals)}
			to
			{addDollarSign(getNumBackInUnits(ord?.limitPrice, 18, marketDecimals))}
		{:else if ord.orderType == 'Advanced'}
			{#if ord.priceB && new BigNumber(ord.priceB).gte(LARGE_POSITIVE_INT)}
				≥ {addDollarSign(getNumBackInUnits(ord?.priceA, 18, marketDecimals), marketDecimals)}
			{:else if ord.priceA && ord.priceA == '1'}
				≤ {addDollarSign(getNumBackInUnits(ord?.priceB, 18, marketDecimals), marketDecimals)}
			{:else if ord.priceA && ord.priceB}
				{addDollarSign(getNumBackInUnits(ord?.priceA, 18, marketDecimals), marketDecimals)} to
				{addDollarSign(getNumBackInUnits(ord?.priceB, 18, marketDecimals), marketDecimals)}
			{:else}
				-
			{/if}
		{:else}
			{addDollarSign(
				getNumBackInUnits(getMarketCurrentMarketPrice(ord.marketIdentifier), 18, marketDecimals)
			)}
		{/if}
	</div>
	<div class="text-fx-grey-100 text-xs font-medium text-center">
		{#if new BigNumber(takeProfit).gt(0) || new BigNumber(stopLoss).gt(0)}
			<div class="text-fx-gradient-23 text-xs font-medium text-center">
				{new BigNumber(takeProfit).gt(0)
					? new BigNumber(takeProfit).gte(1e24)
						? '$' +
						  Number.parseFloat(getNumBackInUnits(takeProfit, 18, marketDecimals)).toExponential(2)
						: addDollarSign(getNumBackInUnits(takeProfit, 18, marketDecimals), marketDecimals)
					: '-'}
			</div>
			<div class="text-fx-gradient-22 text-xs font-medium text-center">
				{new BigNumber(stopLoss).gt(0)
					? new BigNumber(stopLoss).gte(1e24)
						? '$' +
						  Number.parseFloat(getNumBackInUnits(stopLoss, 18, marketDecimals)).toExponential(2)
						: addDollarSign(getNumBackInUnits(stopLoss, 18, marketDecimals), marketDecimals)
					: '-'}
			</div>
		{:else}
			-
		{/if}
	</div>
	<div
		class="text-fx-yellow text-xs font-medium text-center my-auto"
		class:text-fx-pink={cancellable}
	>
		{getOrderStatus()}
	</div>
	{#if cancellable && isCancelling}
		<div class="ml-auto flex gap-2 text-fx-grey-200 text-sm align-middle">
			<div class="flex items-center">
				<ProcessingIcon />
				<div class="pl-1">Cancelling</div>
			</div>
		</div>
	{:else if $retryingOrder}
		<div class="ml-auto flex gap-2 text-fx-grey-200 text-sm align-middle">
			<div class="flex items-center">
				<ProcessingIcon />
				<div class="pl-1">Retrying</div>
			</div>
		</div>
	{:else if cancellable}
		<div class="flex flex-row-reverse gap-2">
			<button
				class="text-fx-pink text-sm text-center cursor-pointer underline underline-offset-2"
				on:click={cancelOrder}
			>
				Cancel
			</button>

			{#if ord && ord.orderType === 'DelayedOffchain'}
				<button
					class="text-fx-pink text-sm text-center cursor-pointer underline underline-offset-2"
					on:click={retryOrder}
				>
					Retry
				</button>
			{/if}
		</div>
	{:else if $manuallyExecutingOrder}
		<div class="ml-auto flex gap-2 text-fx-grey-200 text-sm align-middle">
			<div class="flex items-center">
				<ProcessingIcon />
				<div class="pl-1">Executing</div>
			</div>
		</div>
	{:else if checkIfManuallyExecutable(ord?.timestamp, orderMinAge) && !cancellable}
		<button
			class="text-fx-pink text-sm text-center cursor-pointer underline underline-offset-2"
			on:click={manualExecution}>Manually execute</button
		>
	{:else}
		<div class="text-right">-</div>
	{/if}

	{#if ord.lastErrorMessage}
		<div
			class="grid text-xs text-fx-yellow col-span-7 m-2 px-4 py-2 border border-fx-grey-500 rounded-xl squircle-xl break-all truncate"
		>
			Order execution failed due to {ord.lastErrorMessage} will be retried again. If this error persists
			please contact support.
		</div>
	{/if}
</div>

<!-- Mobile View -->
<div class="sm:hidden block mb-1 p-1">
	<GradientBorder
		gradient="bg-fx-gradient-6"
		paddingClass="p-px"
		borderRadius="rounded-xl "
		outerClassList=" w-full h-full"
		innerClassList="flex justify-center items-center bg-fx-black w-full h-full"
	>
		<div class="bg-fx-gradient-8 w-full h-gull rounded-xl p-4 flex-col">
			<div class="flex justify-between mb-1">
				<div class="flex-col items-start mb-2">
					<div class="flex gap-2">
						<img
							loading="lazy"
							src={getTokenLogo(ord.marketIdentifier)}
							alt="logo"
							class="w-5 h-5 rounded-full"
						/>
						<div class="text-fx-grey-100 text-sm rounded-lg font-medium">
							{getDisplayName(ord.marketIdentifier)}
						</div>
						<GradientBorder
							gradient="bg-fx-gradient-6"
							paddingClass="p-px"
							borderRadius="rounded-lg "
							outerClassList="w-fit h-fit"
							innerClassList="/''bg-fx-black"
						>
							{#if ord.status == 'FirstExecuted' && ord.totalFee ? new BigNumber(ord.size).lt(0) : new BigNumber(ord.size).gt(0)}
								<div
									class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg squircle-lg"
								>
									Long
								</div>
							{:else}
								<div
									class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg squircle-lg"
								>
									Short
								</div>
							{/if}
						</GradientBorder>
					</div>
				</div>
				<div class="flex flex-col items-start">
					<div class="flex gap-2">
						<div>
							<div class="text-fx-grey-100 text-xs font-medium text-right">
								{new BigNumber(ord.size).lte(new BigNumber(LARGE_NEGATIVE_INT)) ||
								new BigNumber(ord.size).gte(new BigNumber(LARGE_POSITIVE_INT))
									? 'Entrie Position'
									: getNumBackInUnits(ord?.size, 18, 4) + ' ' + getDisplayAsset(ord.asset)}
							</div>
							{#if !(new BigNumber(ord.size).lte(new BigNumber(LARGE_NEGATIVE_INT)) || new BigNumber(ord.size).gte(new BigNumber(LARGE_POSITIVE_INT)))}
								<div class="text-fx-grey-300 text-xs font-medium text-right">
									(${getNumBackInUnits(
										new BigNumber(ord.size)
											.multipliedBy(getMarketCurrentMarketPrice(ord.marketIdentifier))
											.div(1e18)
											.abs()
											.toString(),
										18,
										2
									)})
								</div>
							{/if}
						</div>
					</div>
				</div>
			</div>

			<div class="grid grid-cols-3 mt-2">
				<div class="flex-col">
					<div class="text-xs text-fx-grey-400">Execution Price</div>
					<div class="text-sm text-fx-grey-200 font-medium">
						{#if ord.orderType == 'Limit'}
							{addDollarSign(
								getNumBackInUnits(ord?.triggerPrice, 18, marketDecimals),
								marketDecimals
							)}
							to
							{addDollarSign(
								getNumBackInUnits(ord?.limitPrice, 18, marketDecimals),
								marketDecimals
							)}
						{:else if ord.orderType == 'Advanced'}
							{#if ord.priceB && ord.priceB <= LARGE_POSITIVE_INT}
								≥ {addDollarSign(
									getNumBackInUnits(ord?.priceA, 18, marketDecimals),
									marketDecimals
								)}
							{:else if ord.priceA && ord.priceA == '1'}
								≤ {addDollarSign(
									getNumBackInUnits(ord?.priceB, 18, marketDecimals),
									marketDecimals
								)}
							{:else if ord.priceA && ord.priceB}
								{addDollarSign(getNumBackInUnits(ord?.priceA, 18, marketDecimals), marketDecimals)} to
								{addDollarSign(getNumBackInUnits(ord?.priceB, 18, marketDecimals), marketDecimals)}
							{:else}
								-
							{/if}
						{:else}
							{addDollarSign(
								getNumBackInUnits(
									getMarketCurrentMarketPrice(ord.marketIdentifier),
									18,
									marketDecimals
								)
							)}
						{/if}
					</div>
				</div>

				<div class="flex-col text-center">
					<div class="text-xs text-fx-grey-400 text-center">Status</div>
					<div class="text-sm text-fx-grey-200 font-medium text-center">
						{getOrderStatus()}
					</div>
				</div>
				<div class="flex-col text-right">
					<div class="text-xs text-fx-grey-400 text-end">Type</div>
					<div class="text-sm text-fx-grey-200 font-medium text-end">
						{getOrderType()}
					</div>
				</div>

				<div class="grid mt-2 grid-cols-1 flex items-center">
					{#if cancellable && isCancelling}
						<div class="flex text-fx-grey-200 text-sm">
							<div class="flex items-center gap-1">
								<ProcessingIcon />
								<div>Cancelling</div>
							</div>
						</div>
					{:else if $retryingOrder}
						<div class="flex text-fx-grey-200 text-sm">
							<div class="flex items-center gap-1">
								<ProcessingIcon />
								<div>Retrying</div>
							</div>
						</div>
					{:else if cancellable}
						<div class="flex justify-between gap-2">
							<div>
								<button
									class=" border-fx-grey-500 border rounded-md text-center px-6 py-1 text-xs text-fx-pink"
									on:click={cancelOrder}
								>
									Cancel
								</button>
							</div>
							{#if ord && ord.orderType === 'DelayedOffchain'}
								<div>
									<button
										class=" border-fx-grey-500 border rounded-md text-center px-6 py-1 text-xs text-fx-pink"
										on:click={retryOrder}
									>
										Retry
									</button>
								</div>
							{/if}
						</div>
					{:else if $manuallyExecutingOrder}
						<div class="flex text-fx-grey-200 text-sm">
							<div class="flex items-center gap-1">
								<ProcessingIcon />
								<div>Executing</div>
							</div>
						</div>
					{:else if checkIfManuallyExecutable(ord?.timestamp, orderMinAge) && !cancellable}
						<div>
							<button
								class="border-fx-grey-500 border rounded-md text-center px-6 py-1 text-xs text-fx-pink"
								on:click={manualExecution}
							>
								Manually Execute
							</button>
						</div>
					{:else}
						<div class="text-right">-</div>
					{/if}
				</div>
				<div />

				{#if new BigNumber(takeProfit).gt(0) || new BigNumber(stopLoss).gt(0)}
					<div class="flex-col text-right mt-2">
						<div class="text-xs text-fx-grey-400 text-end">TP/SL</div>
						<div class="text-sm text-fx-grey-200 font-medium text-end">
							<div class="text-fx-gradient-23 text-xs font-medium">
								{new BigNumber(takeProfit).gt(0)
									? new BigNumber(takeProfit).gte(1e24)
										? '$' +
										  Number.parseFloat(
												getNumBackInUnits(takeProfit, 18, marketDecimals)
										  ).toExponential(2)
										: addDollarSign(
												getNumBackInUnits(takeProfit, 18, marketDecimals),
												marketDecimals
										  )
									: '-'}
							</div>
							<div class="text-fx-gradient-22 text-xs font-medium">
								{new BigNumber(stopLoss).gt(0)
									? new BigNumber(stopLoss).gte(1e24)
										? '$' +
										  Number.parseFloat(
												getNumBackInUnits(stopLoss, 18, marketDecimals)
										  ).toExponential(2)
										: addDollarSign(getNumBackInUnits(stopLoss, 18, marketDecimals), marketDecimals)
									: '-'}
							</div>
						</div>
					</div>
				{/if}
			</div>
		</div>
	</GradientBorder>
</div>
